import React, { useState } from 'react';
import { WeekRangeFilter } from './ui/week-filter';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Edit, Trash2, Archive, MapPin, X, Printer, QrCode, Plus, FileSpreadsheet, Database } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import ViewToggle, { readStoredView } from './shared/ViewToggle';
import { CopyIconButton, CopyAllButton, AddressActions, formatAddress } from './shared/CopyableLabel';
import FloatingActions from './FloatingActions';
import { exportView, exportFullDump } from '../lib/exportXlsx';
import { useOnSavedViewsReset } from '../hooks/useSavedView';
import RecordReportDialog from './RecordReportDialog';
import { Input } from './ui/input';
import ProjectForm from './ProjectForm';
import ProjectMap from './ProjectMap';
import AttachmentManager from './AttachmentManager';
import ContractsCrossRefPanel from './ContractsCrossRefPanel';
import SurveysList from './SurveysList';
import ProjectChecklists from './ProjectChecklists';
import HireCosts from './inventory/HireCosts';
import ProjectTestReports from './ProjectTestReports';
import ProjectDamageReports from './ProjectDamageReports';
import ProjectTasks from './ProjectTasks';
import ProjectAuditTab from './ProjectAuditTab';
import MachinesPanel from './MachinesPanel';
import { QrLabelDialog } from './QrLabelDialog';
import usePermissions from '../hooks/usePermissions';
import useRememberedInputs from '../hooks/useRememberedInputs';
import PinShortcutButton from './PinShortcutButton';
import PermButton from './shared/PermButton';
import { PROJECT_TAB_META, autoColor, metaLabel } from '../constants/shortcutTargets';
import { patchSession } from '../lib/sessionState';
import { DateText } from './ui/week-badge';
import { fmtDateWeek } from '../lib/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_END_CLIENT = {
  name: '', representative_name: '', representative_position: '',
  phone: '', email: '',
  address: '', city: '', state: '', postal_code: '', country: '',
};

const EMPTY_PROJECT_FORM = {
  name: '', project_number: '', customer_project_number: '',
  description: '', url: '', customer_id: '',
  start_date: new Date().toISOString().split('T')[0],
  planned_end_date: null, actual_end_date: null,
  site_address: '', site_city: '', site_state: '', site_postal_code: '', site_country: '',
  end_client: { ...EMPTY_END_CLIENT },
  customer_contact: { name: '', position: '', phone: '', email: '' },
  site_contact: { name: '', position: '', phone: '', email: '' },
  mechanical_supervisor: { name: '', position: '', phone: '', email: '' },
  electrical_supervisor: { name: '', position: '', phone: '', email: '' },
  hourly_rate: '', currency: 'EUR',
  budget_type: 'hourly', budget_amount: '', budget_currency: 'EUR',
  owner_id: null, status: 'active',
  team_members: []
};

const hasSiteAddress = (p) => p?.site_address || p?.site_city || p?.site_country;

// Street, postal code, city, region, country — the order a maps app reads best.
const siteParts = (p) => [p?.site_address, p?.site_postal_code, p?.site_city, p?.site_state, p?.site_country];

const populateForm = (p) => ({
  id: p.id,
  name: p.name,
  project_number: p.project_number || '',
  customer_project_number: p.customer_project_number || '',
  description: p.description || '', url: p.url || '',
  customer_id: p.customer_id,
  start_date: p.start_date ? p.start_date.split('T')[0] : '',
  planned_end_date: p.planned_end_date ? p.planned_end_date.split('T')[0] : null,
  actual_end_date: p.actual_end_date ? p.actual_end_date.split('T')[0] : null,
  site_address: p.site_address || '', site_city: p.site_city || '', site_state: p.site_state || '',
  site_postal_code: p.site_postal_code || '', site_country: p.site_country || '',
  end_client: { ...EMPTY_END_CLIENT, ...(p.end_client || {}) },
  customer_contact: p.customer_contact || { name: '', position: '', phone: '', email: '' },
  site_contact: p.site_contact || { name: '', position: '', phone: '', email: '' },
  mechanical_supervisor: p.mechanical_supervisor || { name: '', position: '', phone: '', email: '' },
  electrical_supervisor: p.electrical_supervisor || { name: '', position: '', phone: '', email: '' },
  hourly_rate: p.hourly_rate || '', currency: p.currency || 'EUR',
  // Budget fields — previously dropped during edit, causing the
  // currency to silently revert to the model default ("PLN") on save.
  budget_type: p.budget_type || 'hourly',
  budget_amount: p.budget_amount ?? '',
  budget_currency: p.budget_currency || 'EUR',
  owner_id: p.owner_id || null, status: p.status || 'active',
  team_members: p.team_members || []
});

const prepareSubmit = (form) => ({
  ...form,
  start_date: form.start_date ? form.start_date + 'T00:00:00' : new Date().toISOString().split('T')[0] + 'T00:00:00',
  planned_end_date: form.planned_end_date ? form.planned_end_date + 'T00:00:00' : null,
  actual_end_date: form.actual_end_date ? form.actual_end_date + 'T00:00:00' : null,
  hourly_rate: form.hourly_rate === '' || form.hourly_rate === null ? null : parseFloat(form.hourly_rate),
  budget_amount: form.budget_amount === '' || form.budget_amount === null ? null : parseFloat(form.budget_amount),
});

const ProjectsTab = ({ projects, customers, employees, settings, user, fetchData, navContext }) => {
  const { can } = usePermissions();
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...EMPTY_PROJECT_FORM });
  const [editingProject, setEditingProject] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(null);
  const [editDefaultTab, setEditDefaultTab] = useState('details');
  const [searchFilter, setSearchFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('current');
  useRememberedInputs('projects-filters', {
    statusFilter: [statusFilter, setStatusFilter], dateFrom: [dateFrom, setDateFrom], dateTo: [dateTo, setDateTo],
  });
  const [reportProject, setReportProject] = useState(null);
  const [qrProject, setQrProject] = useState(null);
  const [activeProjectTab, setActiveProjectTab] = useState(null);
  const [view, setView] = useState(() => readStoredView('view:projects', 'cards'));
  const [checklistFocus, setChecklistFocus] = useState('');

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  // Compute lifecycle status from dates: planned / current / finished.
  const computeLifecycleStatus = (p) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = p.start_date ? new Date(p.start_date) : null;
    const actualEnd = p.actual_end_date ? new Date(p.actual_end_date) : null;
    const plannedEnd = p.planned_end_date ? new Date(p.planned_end_date) : null;
    if (actualEnd && actualEnd <= today) return 'finished';
    if (plannedEnd && plannedEnd < today && !actualEnd) return 'finished';
    if (start && start > today) return 'planned';
    return 'current';
  };

  const LIFECYCLE_BADGE = {
    planned:  { label: t('auto.lbl.planned'),  cls: 'bg-blue-100 text-blue-700' },
    current:  { label: t('auto.lbl.current'),  cls: 'bg-green-100 text-green-700' },
    finished: { label: t('auto.lbl.finished'), cls: 'bg-gray-200 text-gray-700' },
  };

  const filteredProjects = projects.filter((p) => {
    if (searchFilter) {
      const q = searchFilter.toLowerCase();
      const hay = [p.name, p.project_number, p.customer_project_number, p.site_city]
        .filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (statusFilter !== 'all' && computeLifecycleStatus(p) !== statusFilter) return false;
    if (dateFrom) {
      const from = new Date(dateFrom);
      const start = p.start_date ? new Date(p.start_date) : null;
      if (!start || start < from) return false;
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      const start = p.start_date ? new Date(p.start_date) : null;
      if (!start || start > to) return false;
    }
    return true;
  });

  const EXPORT_COLUMNS = [
    { header: t('auto.lbl.projectNumber'), key: 'project_number' },
    { header: t('auto.lbl.clientReference'), key: 'customer_project_number' },
    { header: t('auto.lbl.name'), key: 'name' },
    { header: t('auto.lbl.customer'), value: (p) => customers.find((c) => c.id === p.customer_id)?.name || '' },
    { header: t('auto.lbl.status'), value: (p) => LIFECYCLE_BADGE[computeLifecycleStatus(p)].label },
    { header: t('auto.lbl.started'), key: 'start_date' },
    { header: t('auto.lbl.plannedEnd'), key: 'planned_end_date' },
    { header: t('auto.lbl.actualEnd'), key: 'actual_end_date' },
    { header: t('auto.lbl.siteAddress'), value: (p) => formatAddress(siteParts(p)) },
  ];

  const openProjectDetails = (project) => {
    setEditingProject(project);
    setForm(populateForm(project));
    setEditDefaultTab('details');
    setEditOpen(project.id);
  };

  // Click handler for the card body. Ignores clicks on interactive children
  // (buttons, links) so the action buttons inside still work as expected.
  const handleCardClick = (project) => (e) => {
    if (e.target.closest('button, a, [role="dialog"], [data-no-card-click]')) return;
    openProjectDetails(project);
  };

  // "Reset my filters" should also clear this list's transient filters,
  // otherwise the button visibly lies about what it resets.
  useOnSavedViewsReset(() => {
    setSearchFilter('');
    setStatusFilter('current');
    setDateFrom('');
    setDateTo('');
  });

  // Deep links (global search, dashboard widgets). Handled once per
  // navContext so a background data refresh can't re-open the dialog.
  const handledNavRef = React.useRef(null);
  React.useEffect(() => {
    if (!navContext || handledNavRef.current === navContext) return;
    if (navContext.searchProject) setSearchFilter(navContext.searchProject);
    if (navContext.openProjectId) {
      const target = projects.find((p) => p.id === navContext.openProjectId);
      if (!target) return; // projects may still be loading — retry next render
      handledNavRef.current = navContext;
      // A deep-linked project may be finished/planned, which the default
      // "current" filter would hide. Show everything so it's findable.
      setStatusFilter('all');
      setEditingProject(target);
      setForm(populateForm(target));
      setEditDefaultTab(navContext.projectTab || 'details');
      setActiveProjectTab(navContext.projectTab || 'details');
      setEditOpen(target.id);
      return;
    }
    handledNavRef.current = navContext;
  }, [navContext, projects]);

  // Breadcrumb for session restore: if the phone kills the tab while a
  // project is open, the next launch comes straight back here instead of the
  // dashboard. Cleared when the dialog closes.
  React.useEffect(() => {
    if (editOpen && editingProject) {
      patchSession({
        navContext: {
          openProjectId: editingProject.id,
          projectTab: activeProjectTab || editDefaultTab || 'details',
          focusChecklistId: checklistFocus || undefined,
        },
      });
    }
  }, [editOpen, editingProject, activeProjectTab, editDefaultTab, checklistFocus]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/projects`, prepareSubmit(form));
      toast.success(t('projects.toasts.created'));
      setCreateOpen(false);
      setForm({ ...EMPTY_PROJECT_FORM });
      fetchData();
    } catch (err) { toast.error(t('auto.proj.errorCreatingProject') + (err.response?.data?.detail || err.message)); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/projects/${editingProject.id}`, prepareSubmit(form));
      toast.success(t('projects.toasts.updated'));
      setEditingProject(null);
      setEditOpen(null);
      setForm({ ...EMPTY_PROJECT_FORM });
      fetchData();
    } catch (err) { toast.error(t('auto.proj.errorUpdatingProject') + (err.response?.data?.detail || err.message)); }
  };

  const handleDelete = async (id, force = false) => {
    try {
      await axios.delete(`${API}/projects/${id}${force ? '?force=true' : ''}`);
      toast.success(t('projects.toasts.deleted'));
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      if (msg.includes('Please delete') || msg.includes('Cannot delete project')) {
        if (window.confirm(`${msg}\n\nForce delete all associated data? This cannot be undone.`)) return handleDelete(id, true);
      } else if (err.response?.status === 403) toast.error(t('auto.proj.permissionDenied'));
      else toast.error(t('auto.proj.errorDeletingProject') + msg);
    }
  };

  const handleArchive = async (id, name) => {
    if (!window.confirm(`Archive "${name}"? It will be hidden but can be restored from Settings > Archived.`)) return;
    try {
      await axios.put(`${API}/projects/${id}/archive`);
      toast.success(`${name} has been archived`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error archiving project'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{t('projects.title')}</h2>
          <p className="text-slate-600">{t('auto.proj.manageActiveProjects')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <ViewToggle storageKey="view:projects" value={view} onChange={setView} />
          <Input value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} placeholder={t('projects.searchPlaceholder')} className="flex-1 sm:w-48" data-testid="project-search-input" />
          {searchFilter && <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setSearchFilter('')}><X className="w-4 h-4" /></Button>}
        {isAdmin && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild><PermButton perm="projects.create" className="shrink-0" data-testid="btn-add-project">{t('projects.addProject')}</PermButton></DialogTrigger>
            <DialogContent className="max-w-[95vw] sm:max-w-[1200px] max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('projects.addProject')}</DialogTitle>
                <DialogDescription>{t('auto.proj.createANewProjectWithDetailed')}</DialogDescription>
              </DialogHeader>
              <ProjectForm projectForm={form} setProjectForm={setForm} customers={customers} employees={employees}
                settings={settings} currentUser={user} onSubmit={handleCreate} isEdit={false}
                otherProjects={projects} />
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      <Card className="bg-white border-0 shadow-sm">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
            <div>
              <CardTitle>{t('auto.proj.activeProjects')}</CardTitle>
              <CardDescription>{t('auto.proj.currentOngoingProjects')}</CardDescription>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex flex-col gap-1 w-full sm:w-auto">
                <label className="text-xs text-slate-500">{t('auto.proj.status')}</label>
                <select
                  className="border rounded h-9 px-2 text-sm bg-white w-full sm:w-auto"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  data-testid="project-status-filter"
                  data-no-card-click
                >
                  <option value="all">{t('auto.proj.allStatuses')}</option>
                  <option value="planned">{t('auto.proj.planned')}</option>
                  <option value="current">{t('auto.proj.current')}</option>
                  <option value="finished">{t('auto.proj.finished')}</option>
                </select>
              </div>
              <div className="flex flex-col gap-1 w-full sm:w-auto">
                <label className="text-xs text-slate-500">{t('auto.proj.startedFrom')}</label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-full sm:w-[170px]" data-testid="project-date-from" />
              </div>
              <div className="flex flex-col gap-1 w-full sm:w-auto">
                <label className="text-xs text-slate-500">{t('auto.proj.startedTo')}</label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-full sm:w-[170px]" data-testid="project-date-to" />
              </div>
              <div className="flex flex-col gap-1 w-full sm:w-auto">
                <label className="text-xs text-slate-500">{t('weekFilter.week')}</label>
                <WeekRangeFilter from={dateFrom} to={dateTo} onChange={(f, t2) => { setDateFrom(f); setDateTo(t2); }} testId="project-week-filter" />
              </div>
              {(dateFrom || dateTo || statusFilter !== 'current') && (
                <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); setStatusFilter('current'); }} data-testid="project-clear-filters">
                  <X className="w-4 h-4 mr-1" />{t('auto.proj.clear')}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredProjects.length === 0 && (
            <div className="text-center py-8 text-slate-500" data-testid="project-empty-state">
              {t('auto.proj.noProjectsMatchTheCurrentFilters')}
            </div>
          )}

          {view === 'list' && filteredProjects.length > 0 && (
            <div>
              <Table data-testid="projects-list-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('auto.proj.number')}</TableHead>
                    <TableHead>{t('auto.proj.name')}</TableHead>
                    <TableHead className="hidden md:table-cell">{t('auto.proj.customer')}</TableHead>
                    <TableHead className="hidden sm:table-cell">{t('auto.proj.status')}</TableHead>
                    <TableHead className="hidden lg:table-cell">{t('auto.proj.started')}</TableHead>
                    <TableHead className="hidden xl:table-cell">{t('auto.proj.plannedEnd')}</TableHead>
                    <TableHead className="hidden lg:table-cell">{t('auto.proj.site')}</TableHead>
                    <TableHead className="text-right w-[116px] sm:w-[156px] lg:w-[196px]">{t('auto.proj.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProjects.map((project) => {
                    const customer = customers.find(c => c.id === project.customer_id);
                    const lifecycle = computeLifecycleStatus(project);
                    return (
                      <TableRow key={project.id} className="cursor-pointer hover:bg-slate-50"
                        data-testid={`project-row-${project.id}`}
                        onClick={(e) => { if (!e.target.closest('button, a')) openProjectDetails(project); }}>
                        <TableCell className="font-mono text-xs whitespace-nowrap align-top">
                          <span className="inline-flex items-center gap-1">
                            {project.project_number || '—'}
                            <CopyIconButton label="Project number" value={project.project_number} testId={`copy-row-number-${project.id}`} />
                          </span>
                          {project.customer_project_number && (
                            <span className="block text-[11px] text-slate-500">
                              {project.customer_project_number}
                              <CopyIconButton label="Client reference" value={project.customer_project_number} testId={`copy-row-client-ref-${project.id}`} />
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="font-medium break-words align-top">
                          {project.name}
                          {/* Columns hidden on small screens fold in under the name. */}
                          <span className="md:hidden block text-xs text-slate-500 break-words">{customer?.name || ''}</span>
                          <span className="sm:hidden block text-xs text-slate-500">{LIFECYCLE_BADGE[lifecycle].label}</span>
                          <span className="lg:hidden block text-xs text-slate-500">
                            {[project.site_city, project.site_country].filter(Boolean).join(', ')}
                            <AddressActions parts={siteParts(project)} label={t('auto.proj.siteAddress')}
                              testId={`copy-fold-site-address-${project.id}`} />
                          </span>
                        </TableCell>
                        <TableCell className="break-words hidden md:table-cell align-top">{customer?.name || '—'}</TableCell>
                        <TableCell className="hidden sm:table-cell align-top">
                          <span className={`px-2 py-0.5 rounded text-xs ${LIFECYCLE_BADGE[lifecycle].cls}`}>
                            {LIFECYCLE_BADGE[lifecycle].label}
                          </span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm hidden lg:table-cell align-top">
                          <DateText value={project.start_date} />
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm hidden xl:table-cell align-top">
                          <DateText value={project.planned_end_date} />
                        </TableCell>
                        <TableCell className="text-sm break-words hidden lg:table-cell align-top">
                          <span className="inline-flex items-start gap-1 flex-wrap">
                            {[project.site_city, project.site_country].filter(Boolean).join(', ') || '—'}
                            <AddressActions parts={siteParts(project)} label={t('auto.proj.siteAddress')}
                              testId={`copy-row-site-address-${project.id}`} />
                          </span>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex items-center justify-end gap-1 flex-wrap w-[112px] sm:w-[152px] lg:w-[188px] ml-auto">
                            <CopyAllButton
                              testId={`copy-all-project-${project.id}`}
                              title={project.name}
                              lines={[
                                ['Project number', project.project_number],
                                ['Client reference', project.customer_project_number],
                                ['Name', project.name],
                                ['Customer', customer?.name],
                                ['Site', [project.site_address, project.site_city, project.site_country].filter(Boolean).join(', ')],
                                ['Started', fmtDateWeek(project.start_date)],
                              ]} />
                            {isAdmin && (
                              <Button variant="ghost" size="icon" className="h-8 w-8" title={t('auto.proj.editProject')}
                                data-testid={`project-row-edit-${project.id}`}
                                onClick={() => openProjectDetails(project)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" className="h-8 w-8" title={t('auto.proj.qrLabel')}
                              data-testid={`project-row-qr-${project.id}`}
                              onClick={() => setQrProject(project)}>
                              <QrCode className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" title={t('auto.proj.printReport')}
                              data-testid={`project-row-report-${project.id}`}
                              onClick={() => setReportProject(project)}>
                              <Printer className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          <div className={`space-y-4 ${view === 'list' ? 'hidden' : ''}`}>
            {filteredProjects.map((project) => {
              const customer = customers.find(c => c.id === project.customer_id);
              const lifecycle = computeLifecycleStatus(project);
              const lifecycleBadge = LIFECYCLE_BADGE[lifecycle];
              return (
                <div
                  key={project.id}
                  className="p-4 border border-slate-200 rounded-lg cursor-pointer hover:border-slate-400 hover:shadow-sm transition"
                  data-testid={`project-card-${project.id}`}
                  role="button"
                  tabIndex={0}
                  onClick={handleCardClick(project)}
                  onKeyDown={(e) => { if (e.key === 'Enter') openProjectDetails(project); }}
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-3 mb-3">
                    <div className="min-w-0 w-full sm:flex-1">
                      <div className="flex items-center gap-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 break-words font-mono text-sm" data-testid={`project-number-${project.id}`}>
                          {project.project_number || '—'}
                        </h3>
                        <CopyIconButton label="Project number" value={project.project_number} testId={`copy-project-number-${project.id}`} />
                      </div>
                      <p className="text-slate-800 break-words">{project.name}</p>
                      {project.customer_project_number && (
                        <p className="text-xs text-slate-500 flex items-center gap-1 break-words">
                          Client ref: {project.customer_project_number}
                          <CopyIconButton label="Client reference" value={project.customer_project_number} testId={`copy-client-ref-${project.id}`} />
                        </p>
                      )}
                      <div className="flex flex-wrap gap-1 mt-1">
                        <Badge variant="secondary">{project.status}</Badge>
                        <span className={`px-2 py-0.5 rounded text-xs ${lifecycleBadge.cls}`} data-testid={`project-lifecycle-${project.id}`}>
                          {lifecycleBadge.label}
                        </span>
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="flex flex-wrap justify-end gap-1 w-full sm:w-auto sm:shrink-0" data-no-card-click onClick={(e) => e.stopPropagation()}>
                        {hasSiteAddress(project) && (
                          <Button variant="outline" size="sm" title="View on map" data-testid={`project-map-btn-${project.id}`}
                            onClick={() => { setEditingProject(project); setForm(populateForm(project)); setEditDefaultTab('location'); setActiveProjectTab('location'); setEditOpen(project.id); }}>
                            <MapPin className="w-4 h-4 text-blue-600" />
                          </Button>
                        )}
                        <Button variant="outline" size="sm" title={t('auto.proj.editProject')}
                          data-testid={`project-edit-${project.id}`}
                          onClick={() => openProjectDetails(project)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" title={t('auto.proj.printReport')}
                          data-testid={`report-project-${project.id}`}
                          onClick={(e) => { e.stopPropagation(); setReportProject(project); }}>
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" title={t('auto.proj.qrLabel')}
                          data-testid={`qr-project-${project.id}`}
                          onClick={(e) => { e.stopPropagation(); setQrProject(project); }}>
                          <QrCode className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild><PermButton perm="projects.delete" variant="outline" size="sm" title="Delete permanently" data-testid={`delete-project-${project.id}`}><Trash2 className="w-4 h-4 text-red-500" /></PermButton></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t('auto.proj.deleteProject')}</AlertDialogTitle>
                              <AlertDialogDescription>Are you sure you want to delete &quot;{project.name}&quot;? This cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t('auto.proj.cancel')}</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(project.id)}>{t('auto.proj.delete')}</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <PermButton perm="projects.edit" variant="outline" size="sm" title={t('auto.proj.archiveProject')}
                          data-testid={`archive-project-${project.id}`}
                          onClick={() => handleArchive(project.id, project.name)}>
                          <Archive className="w-4 h-4 text-orange-500" />
                        </PermButton>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mb-2 break-words">{project.description}</p>
                  {hasSiteAddress(project) && (
                    <p className="text-sm text-slate-600 mb-2 flex items-start gap-1 break-words"
                      data-testid={`project-site-address-${project.id}`}>
                      <span className="text-slate-400 shrink-0">{t('auto.proj.site2')}</span>
                      <span className="break-words">{formatAddress(siteParts(project))}</span>
                      <AddressActions parts={siteParts(project)} label={t('auto.proj.siteAddress')}
                        testId={`copy-site-address-${project.id}`} />
                    </p>
                  )}
                  {project.url && (
                    <div className="mb-2">
                      <a href={project.url.startsWith('http') ? project.url : `https://${project.url}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline truncate block"
                        data-testid={`project-url-${project.id}`}>{project.url}</a>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-3 text-sm text-slate-500">
                    <span className="break-words"><span className="text-slate-400">{t('auto.proj.customer2')}</span> {customer?.name || 'Unknown'}</span>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                      <span className="whitespace-nowrap">{t('auto.proj.started2')} <DateText value={project.start_date} /></span>
                      {project.actual_end_date && <span className="whitespace-nowrap text-green-600">{t('auto.proj.ended')} <DateText value={project.actual_end_date} /></span>}
                      {!project.actual_end_date && project.planned_end_date && <span className="whitespace-nowrap text-orange-500">{t('auto.proj.plannedEnd2')} <DateText value={project.planned_end_date} /></span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      {editingProject && (
    <Dialog open={!!editingProject} onOpenChange={(open) => {
      if (!open) { setEditOpen(null); setEditingProject(null); setEditDefaultTab('details'); setActiveProjectTab(null); setChecklistFocus(''); setForm({ ...EMPTY_PROJECT_FORM }); patchSession({ navContext: null }); }
    }}>
      <DialogContent className="top-0 left-0 right-0 translate-x-0 translate-y-0 h-[100dvh] w-screen max-w-none rounded-none content-start sm:content-normal p-4 overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)] sm:top-[50%] sm:left-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:h-auto sm:w-[96vw] sm:max-h-[92vh] sm:max-w-[1800px] sm:rounded-lg sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg pr-8 break-words flex items-center gap-1">
            <span className="min-w-0 break-words">Edit Project - {editingProject.name}</span>
            {(() => {
              const pTab = activeProjectTab || editDefaultTab || 'details';
              const pMeta = PROJECT_TAB_META[pTab] || { icon: 'projects', perms: ['projects.view'] };
              return (
                <PinShortcutButton target={{
                  tab: 'projects',
                  context: { openProjectId: editingProject.id, projectTab: pTab },
                  label: metaLabel(pMeta, t) || pTab,
                  subtitle: editingProject.name,
                  icon: pMeta.icon,
                  color: autoColor(`project-${pTab}`),
                  perms: pMeta.perms,
                }} />
              );
            })()}
          </DialogTitle>
          <DialogDescription className="hidden sm:block">{t('auto.proj.updateProjectInformationAndAttachments')}</DialogDescription>
        </DialogHeader>
        <Tabs value={activeProjectTab || editDefaultTab} className="space-y-4 min-w-0"
          onValueChange={setActiveProjectTab}>
          {/* Ten tabs don't fit a phone, so they scroll
              sideways instead of stacking six rows deep. */}
          <TabsList className="flex w-full min-w-0 max-w-full justify-start gap-1 overflow-x-auto p-1 h-auto flex-nowrap sm:flex-wrap [&>*]:shrink-0 [&::-webkit-scrollbar]:h-1">
            <TabsTrigger value="details" data-testid="tab-details">{t('projects_deep.tabs.details')}</TabsTrigger>
            <TabsTrigger value="location" data-testid="tab-location">{t('projects_deep.tabs.location')}</TabsTrigger>
            {can('tasks.view') && (
              <TabsTrigger value="tasks" data-testid={`project-tasks-tab-${editingProject.id}`}>{t('projects_deep.tabs.tasks')}</TabsTrigger>
            )}
            {can('iso_checklists.view') && (
              <TabsTrigger value="checklists" data-testid="tab-checklists">{t('projects_deep.tabs.checklists')}</TabsTrigger>
            )}
            {can('machines.view') && (
              <TabsTrigger value="machines" data-testid={`project-machines-tab-${editingProject.id}`}>Machines</TabsTrigger>
            )}
            {can('iso_test_reports.view') && (
              <TabsTrigger value="test-reports" data-testid={`project-test-reports-tab-${editingProject.id}`}>{t('projects_deep.tabs.testReports')}</TabsTrigger>
            )}
            {can('damage_reports.view') && (
              <TabsTrigger value="damage-reports" data-testid={`project-damage-reports-tab-${editingProject.id}`}>{t('projects_deep.tabs.damage')}</TabsTrigger>
            )}
            {can('project_documents.view') && (
              <TabsTrigger value="attachments" data-testid="tab-attachments">{t('projects_deep.tabs.attachments')}</TabsTrigger>
            )}
            {can('contracts.view') && (
              <TabsTrigger value="contracts" data-testid={`project-contracts-tab-${editingProject.id}`}>{t('projects_deep.tabs.documents')}</TabsTrigger>
            )}
            {can('hire_costs.view') && (
              <TabsTrigger value="hired-plant" data-testid={`project-hired-plant-tab-${editingProject.id}`}>Hired plant</TabsTrigger>
            )}
            {can('customer_surveys.view') && (
              <TabsTrigger value="surveys" data-testid={`project-surveys-tab-${editingProject.id}`}>{t('projects_deep.tabs.surveys')}</TabsTrigger>
            )}
            {can('audit.view') && (
              <TabsTrigger value="audit" data-testid={`project-audit-tab-${editingProject.id}`}>{t('projects_deep.tabs.audit')}</TabsTrigger>
            )}
          </TabsList>
          <TabsContent value="details">
            <ProjectForm projectForm={form} setProjectForm={setForm} customers={customers} employees={employees}
              settings={settings} currentUser={user} onSubmit={handleEdit} isEdit={true}
              otherProjects={projects} />
          </TabsContent>
          <TabsContent value="location"><ProjectMap project={editingProject} /></TabsContent>
          {can('tasks.view') && (
          <TabsContent value="tasks">
            <ProjectTasks projectId={editingProject.id} projectName={editingProject.name} />
          </TabsContent>
          )}
          {can('iso_checklists.view') && (
          <TabsContent value="checklists">
            <ProjectChecklists projectId={editingProject.id} projectName={editingProject.name}
              focusInstanceId={checklistFocus} />
          </TabsContent>
          )}
          {can('machines.view') && (
          <TabsContent value="machines">
            <MachinesPanel projectId={editingProject.id} isAdmin={user?.role === 'admin'}
              canEdit={can('machines.edit') || can('machines.create')}
              projectNumber={editingProject.project_number}
              focusMachineId={navContext?.focusMachineId}
              onOpenChecklist={(id) => {
                setChecklistFocus(id);
                setActiveProjectTab('checklists');
              }} />
          </TabsContent>
          )}
          {can('iso_test_reports.view') && (
          <TabsContent value="test-reports">
            <ProjectTestReports projectId={editingProject.id} projectName={editingProject.name} token={localStorage.getItem('token')} currentUser={user} />
          </TabsContent>
          )}
          {can('damage_reports.view') && (
          <TabsContent value="damage-reports">
            <ProjectDamageReports projectId={editingProject.id} projectName={editingProject.name} user={user} />
          </TabsContent>
          )}
          {can('project_documents.view') && (
          <TabsContent value="attachments">
            <AttachmentManager entityType="project" entityId={editingProject.id} entityName={editingProject.name} />
          </TabsContent>
          )}
          {can('contracts.view') && (
          <TabsContent value="contracts" className="space-y-3">
            <div>
              <h3 className="text-base font-semibold text-slate-800">{t('auto.proj.linkedDocuments')}</h3>
              <p className="text-xs text-slate-500">{t('auto.proj.contractsAnnexesSupportAndClientLetters')}</p>
            </div>
            <ContractsCrossRefPanel scope="project" id={editingProject.id} />
          </TabsContent>
          )}
          {can('hire_costs.view') && (
            <TabsContent value="hired-plant">
              <HireCosts projectId={editingProject.id} />
            </TabsContent>
          )}
          {can('customer_surveys.view') && (
          <TabsContent value="surveys" className="space-y-3">
            <div>
              <h3 className="text-base font-semibold text-slate-800">{t('auto.proj.customerSatisfactionSurveys')}</h3>
              <p className="text-xs text-slate-500">{t('auto.proj.captureCustomerFeedbackForTheWhole')}</p>
            </div>
            <SurveysList projectId={editingProject.id} />
          </TabsContent>
          )}
          {can('audit.view') && (
          <TabsContent value="audit">
            <ProjectAuditTab projectId={editingProject.id} projectName={editingProject.name} />
          </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
      )}
      <FloatingActions actions={[
        { icon: Plus, label: t('projects.addProject'), tone: 'primary',
          testId: 'projects-floating-add-btn', hidden: !isAdmin,
          onClick: () => setCreateOpen(true) },
        { icon: FileSpreadsheet, label: t('auto.lbl.exportThisViewToExcel'),
          testId: 'projects-floating-export-btn',
          onClick: () => exportView({
            filename: 'projects', sheetName: 'Projects',
            columns: EXPORT_COLUMNS, rows: filteredProjects,
          }) },
        { icon: Database, label: t('auto.lbl.exportAllProjectsWithEveryField'),
          testId: 'projects-floating-export-all-btn', hidden: !isAdmin,
          onClick: () => exportFullDump({ filename: 'projects', sheetName: 'Projects', rows: projects }) },
      ]} />
      <RecordReportDialog
        open={!!reportProject}
        onOpenChange={(o) => !o && setReportProject(null)}
        kind="project"
        recordId={reportProject?.id}
        recordName={reportProject?.name}
      />
      {qrProject && (
        <QrLabelDialog open={!!qrProject} onOpenChange={(o) => !o && setQrProject(null)}
          kind="project" id={qrProject.id} title={qrProject.name} isAdmin={user?.role === 'admin'} />
      )}
    </div>
  );
};

export default ProjectsTab;
